/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {firstValueFrom, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import * as _ from 'lodash';

import template from './line-item-copy-modal.component.html';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LineItemApiService} from './line-item-api.service';
import {AgentBookingOrdersService} from '../order/agent-booking-orders.service';

export interface LineItemCopyResult {
  lineItemId: string;
  destination: boolean;
}

@Component({
  standalone: true,
  imports: [...ADVERTISING_SHARED],
  selector: 'adn-line-item-copy-modal',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineItemCopyModalComponent implements OnInit, OnDestroy {
  model: any;

  readonly onCopied = new Subject<LineItemCopyResult>();

  lineItem: any = {};
  isMarketplace = false;
  copy = {targeting: true, creatives: true};
  destination = true;
  private readonly disabledState = signal(false);
  private readonly validationErrorsState = signal<any[]>([]);
  get disabled(): boolean { return this.disabledState(); }
  set disabled(value: boolean) { this.disabledState.set(value); }
  get validationErrors(): any[] { return this.validationErrorsState(); }
  set validationErrors(value: any[]) { this.validationErrorsState.set(value); }
  /** The order whose agent booking warning is showing, and whether an agent is known to have booked it. */
  private readonly agentWarningState = signal<{orderId: string; known: boolean} | null>(null);
  private closed = false;

  /** Shown when the chosen order may be an agent's: a second copy into the same order goes ahead. */
  get agentOrderWarning(): {known: boolean} | null {
    const warning = this.agentWarningState();
    return warning && warning.orderId === this.lineItem?.order?.id ? warning : null;
  }

  constructor(
    public bsModalRef: BsModalRef,
    private readonly lineItemApi: LineItemApiService,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any,
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    private readonly agentBookingOrders: AgentBookingOrdersService
  ) {
    this.isMarketplace = LocalNetworkProfile.isMarketplacePlatform();
  }

  ngOnInit(): void {
    this.lineItem = _.cloneDeep(this.model || {});
    this.lineItem.name = nextCopyTitle(this.model?.name || '');
    this.lineItem.userState = 'PROPOSED';
  }

  dismiss(): void {
    this.closed = true;
    this.bsModalRef.hide();
  }

  ngOnDestroy(): void {
    this.closed = true;
  }

  copyCampaign(): void {
    const orderId = this.lineItem?.order?.id;
    if (!orderId || this.agentOrderWarning) {
      this.copyNow();
      return;
    }
    this.disabled = true;
    this.agentBookingOrders.lookUp(orderId).then((agentOrder) => {
      this.disabled = false;
      // Copy nothing when the person closed the dialog or chose another order during the check; the next click checks again.
      if (this.closed || orderId !== this.lineItem?.order?.id) {
        return;
      }
      if (agentOrder === false) {
        this.copyNow();
        return;
      }
      // The agent can change proposed line items in an order that it booked, so the person confirms with a second copy.
      this.agentWarningState.set({orderId, known: agentOrder === true});
    });
  }

  private copyNow(): void {
    this.disabled = true;
    this.validationErrors = [];
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    firstValueFrom(this.lineItemApi.copyLineItem(
      this.lineItem,
      newId,
      this.copy.targeting,
      this.copy.creatives,
      true
    )).then(() => {
      this.disabled = false;
      this.adnModalLoading.removeModalLoading();
      completeCopyModal(this.bsModalRef, this.onCopied, {destination: this.destination, lineItemId: newId});
    }, (errors: any) => {
      this.disabled = false;
      this.validationErrors = (errors && errors.data && errors.data.errors) || [];
      this.adnModalLoading.removeModalLoading();
    });
  }
}
